import { IUser, IUserProfile } from '../types'
import { pb } from '../pocketbase/Pocketbase'

// ============================================================================
// Types
// ============================================================================

export interface IUserUpdateData {
  fullName?: string
  username?: string
  gender?: string
  birthday?: Date | null
  avatar?: File | null
}

// ============================================================================
// Users API
// ============================================================================

/**
 * Fetch a user by ID
 */
export async function fetchUserById(id: string): Promise<IUser> {
  const latestUser = await pb.collection('users').getOne(id)
  const user: IUser = {
    id: latestUser.id,
    email: latestUser.email,
    role: latestUser.role,
    created: latestUser.created,
    deleteRequestedAt: latestUser.deleteRequestedAt
  }
  return user
}

/**
 * Fetch the current authenticated user
 */
export async function fetchCurrentUser(): Promise<IUser | null> {
  const userRecord = pb.authStore.record
  if (!pb.authStore.isValid || !userRecord) {
    return null
  }
  return fetchUserById(userRecord.id)
}

/**
 * Fetch a user profile by user ID
 */
export async function fetchUserProfile(userId: string): Promise<IUserProfile | null> {
  try {
    const profiles = await pb.collection('user_profiles').getList(1, 1, {
      filter: `user = "${userId}"`,
    })

    if (profiles.items.length === 0) {
      return null
    }

    const profile = profiles.items[0]
    return {
      id: profile.id,
      avatar: profile.avatar,
      fullName: profile.fullName,
      username: profile.username,
      gender: profile.gender,
      birthday: profile.birthday,
      user: profile.user,
    }
  } catch (error) {
    console.error('Error fetching user profile:', error)
    return null
  }
}

/**
 * Create a user profile (called on first login)
 */
export async function createUserProfile(
  userId: string,
  fullName: string = '',
  username: string = ''
): Promise<IUserProfile> {
  const profileData = {
    user: userId,
    fullName,
    username,
    avatar: '',
    gender: '',
    birthday: '',
  }

  const createdProfile = await pb.collection('user_profiles').create(profileData)

  return {
    id: createdProfile.id,
    avatar: createdProfile.avatar,
    fullName: createdProfile.fullName,
    username: createdProfile.username,
    gender: createdProfile.gender,
    birthday: createdProfile.birthday,
    user: createdProfile.user,
  }
}

/**
 * Update a user's profile
 */
export async function updateUserProfile(
  userId: string,
  updates: IUserUpdateData
): Promise<IUserProfile> {
  // First, get the user's profile
  let profile = await fetchUserProfile(userId)

  // If no profile exists, create one
  if (!profile) {
    profile = await createUserProfile(userId)
  }

  // Build FormData for the update (needed for file uploads)
  const formData = new FormData()

  if (updates.fullName !== undefined) {
    formData.append('fullName', updates.fullName)
  }
  if (updates.username !== undefined) {
    formData.append('username', updates.username)
  }
  if (updates.gender !== undefined) {
    formData.append('gender', updates.gender)
  }
  if (updates.birthday !== undefined) {
    formData.append('birthday', updates.birthday ? updates.birthday.toISOString() : '')
  }
  if (updates.avatar !== undefined) {
    if (updates.avatar) {
      formData.append('avatar', updates.avatar)
    } else {
      // To remove avatar, send empty string
      formData.append('avatar', '')
    }
  }
  const updatedRecord = await pb.collection('user_profiles').update(profile.id, formData)

  return {
    id: updatedRecord.id,
    avatar: updatedRecord.avatar,
    fullName: updatedRecord.fullName,
    username: updatedRecord.username,
    gender: updatedRecord.gender,
    birthday: updatedRecord.birthday,
    user: updatedRecord.user,
  }
}

/**
 * Update user after OAuth login (creates profile with initial fullName and username)
 */
export async function updateUserAfterOAuth(
  userId: string,
  fullName: string
): Promise<void> {
  // Check if profile exists
  const existingProfile = await fetchUserProfile(userId)

  if (!existingProfile) {
    // Create new profile with OAuth data
    await createUserProfile(userId, fullName)
  } else {
    // Update existing profile if fields are empty
    const updates: IUserUpdateData = {}
    if (!existingProfile.fullName && fullName) {
      updates.fullName = fullName
    }
    if (Object.keys(updates).length > 0) {
      await updateUserProfile(userId, updates)
    }
  }
}

/**
 * Get user avatar URL from profile
 */
export function getUserAvatarUrl(profile: { id: string; avatar: string }): string {
  return pb.files.getURL(
    { id: profile.id, collectionId: 'user_profiles' },
    profile.avatar
  )
}
